const MARK_PREFIX = 'cp';
const activeSpans = new Map();
let spanCounter = 0;
function toLabel(stage) {
    return `${MARK_PREFIX}:${stage}`;
}
function sanitizeContextPart(value) {
    const normalized = String(value ?? '').trim();
    if (!normalized) {
        return null;
    }
    return normalized.replace(/[|=:#\s]+/g, '_');
}
function contextSuffix(context) {
    if (!context) {
        return '';
    }
    const entries = [
        ['proc', sanitizeContextPart(context.process)],
        ['trace', sanitizeContextPart(context.traceId)],
        ['parent', sanitizeContextPart(context.parentSpanId)],
        ['req', sanitizeContextPart(context.requestId)],
        ['rev', sanitizeContextPart(context.fontRevisionKey)]
    ];
    const parts = entries
        .filter(([, value]) => !!value)
        .map(([key, value]) => `${key}=${value}`);
    return parts.length > 0 ? `|${parts.join('|')}` : '';
}
function normalizeSpanOptions(detailOrOptions, explicitContext) {
    if (!detailOrOptions) {
        return { context: explicitContext };
    }
    const candidate = detailOrOptions;
    const candidateKeys = Object.keys(candidate);
    const hasOptionsShape = typeof detailOrOptions === 'object' &&
        detailOrOptions !== null &&
        ('detail' in candidate || 'context' in candidate) &&
        candidateKeys.every((key) => key === 'detail' || key === 'context');
    if (hasOptionsShape) {
        return {
            detail: candidate.detail,
            context: explicitContext ?? candidate.context
        };
    }
    return {
        detail: detailOrOptions,
        context: explicitContext
    };
}
function safeTimeStamp(label) {
    try {
        globalThis.console?.timeStamp?.(label);
    }
    catch {
        // Ignore browsers without timestamp support
    }
}
function canMark() {
    return typeof globalThis.performance?.mark === 'function';
}
function canMeasure() {
    return typeof globalThis.performance?.measure === 'function';
}
export function timelineMark(stage, context) {
    const label = toLabel(stage) + contextSuffix(context);
    if (canMark()) {
        globalThis.performance.mark(label);
    }
    safeTimeStamp(label);
}
export function timelineSpanStart(stage, detailOrOptions, explicitContext) {
    const options = normalizeSpanOptions(detailOrOptions, explicitContext);
    const suffix = contextSuffix(options.context);
    spanCounter += 1;
    const spanId = `${stage}#${spanCounter}`;
    const startMark = toLabel(`${spanId}:start`) + suffix;
    activeSpans.set(spanId, { stage, startMark, contextSuffix: suffix });
    if (canMark()) {
        if (options.detail !== undefined) {
            globalThis.performance.mark(startMark, { detail: options.detail });
        }
        else {
            globalThis.performance.mark(startMark);
        }
    }
    safeTimeStamp(startMark);
    return spanId;
}
export function timelineSpanEnd(spanId) {
    const activeSpan = activeSpans.get(spanId);
    if (!activeSpan) {
        return;
    }
    const endMark = toLabel(`${spanId}:end`) + activeSpan.contextSuffix;
    if (canMark()) {
        globalThis.performance.mark(endMark);
    }
    if (canMeasure()) {
        globalThis.performance.measure(toLabel(activeSpan.stage) + activeSpan.contextSuffix, activeSpan.startMark, endMark);
    }
    safeTimeStamp(endMark);
    activeSpans.delete(spanId);
}
export function timelineWrap(stage, fn) {
    const spanId = timelineSpanStart(stage);
    return fn().finally(() => {
        timelineSpanEnd(spanId);
    });
}
const CODE_LOADING_MARK_PREFIXES = [
    'cp:app.html.',
    'cp:app.head',
    'cp:app.body',
    'cp:app.afterFirstPaint',
    'cp:app.bootstrap.',
    'cp:app.domContentLoaded',
    'cp:python.'
];
function roundMs(value) {
    return Math.round(value * 10) / 10;
}
function isCodeLoadingLabel(name) {
    return CODE_LOADING_MARK_PREFIXES.some((prefix) => name.startsWith(prefix));
}
function shortenResourceName(url) {
    return url
        .replace(/^https:\/\/cdn\.jsdelivr\.net/, 'jsdelivr')
        .replace(/^https?:\/\/[^/]+/, '');
}
function isCodeLoadingResource(entry) {
    const name = entry.name;
    if (/\.(woff2?|ttf|otf)(\?|$)/i.test(name)) {
        return false;
    }
    if (entry.initiatorType === 'script' ||
        entry.initiatorType === 'link' ||
        entry.initiatorType === 'css') {
        return (/\.(js|css|wasm|zip|whl|py|json)(\?|$)/i.test(name) ||
            name.includes('pyodide') ||
            name.includes('/npm/'));
    }
    return (/\.(js|wasm|zip|whl|py)(\?|$)/i.test(name) ||
        name.includes('pyodide') ||
        name.includes('/wheels/'));
}
export function dumpCodeLoadingTimeline() {
    const navigationEntry = performance.getEntriesByType('navigation')[0];
    const marks = performance
        .getEntriesByType('mark')
        .filter((entry) => isCodeLoadingLabel(entry.name))
        .map((entry) => ({
        name: entry.name,
        startTime: roundMs(entry.startTime)
    }));
    const measures = performance
        .getEntriesByType('measure')
        .filter((entry) => isCodeLoadingLabel(entry.name))
        .map((entry) => ({
        name: entry.name,
        startTime: roundMs(entry.startTime),
        duration: roundMs(entry.duration)
    }));
    const resources = performance
        .getEntriesByType('resource')
        .filter((entry) => isCodeLoadingResource(entry))
        .map((entry) => ({
        name: shortenResourceName(entry.name),
        initiator: entry.initiatorType,
        startTime: roundMs(entry.startTime),
        duration: roundMs(entry.duration),
        transferSize: entry.transferSize,
        encodedBodySize: entry.encodedBodySize,
        decodedBodySize: entry.decodedBodySize
    }))
        .sort((a, b) => a.startTime - b.startTime);
    return {
        now: roundMs(performance.now()),
        navigation: navigationEntry
            ? {
                type: navigationEntry.type,
                fetchStart: roundMs(navigationEntry.fetchStart),
                responseEnd: roundMs(navigationEntry.responseEnd),
                domInteractive: roundMs(navigationEntry.domInteractive),
                domContentLoaded: roundMs(navigationEntry.domContentLoadedEventEnd),
                loadEventEnd: roundMs(navigationEntry.loadEventEnd),
                transferSize: navigationEntry.transferSize
            }
            : null,
        marks,
        measures,
        resources
    };
}
if (typeof window !== 'undefined') {
    window.timelineMark = timelineMark;
    window.timelineSpanStart = timelineSpanStart;
    window.timelineSpanEnd = timelineSpanEnd;
    window.dumpCodeLoadingTimeline = dumpCodeLoadingTimeline;
}
